import type { FC } from "react";

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

/**
 * Builds a compact page number list with ellipsis gaps.
 *
 * Example for page=5, totalPages=20, siblings=1:
 *   [1, "…", 4, 5, 6, "…", 20]
 */
function buildPageList(
  page: number,
  totalPages: number,
  siblings: number,
): (number | "…")[] {
  const items: (number | "…")[] = [];
  const left = Math.max(2, page - siblings);
  const right = Math.min(totalPages - 1, page + siblings);

  items.push(1);

  if (left > 2) {
    items.push("…");
  }

  for (let i = left; i <= right; i++) {
    items.push(i);
  }

  if (right < totalPages - 1) {
    items.push("…");
  }

  if (totalPages > 1) {
    items.push(totalPages);
  }

  return items;
}

const ChevronLeft: FC = () => (
  <svg
    className="h-3 w-3"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2.5}
      d="M15 19l-7-7 7-7"
    />
  </svg>
);

const ChevronRight: FC = () => (
  <svg
    className="h-3 w-3"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2.5}
      d="M9 5l7 7-7 7"
    />
  </svg>
);

const ChevronsLeft: FC = () => (
  <svg
    className="h-3 w-3"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2.5}
      d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
    />
  </svg>
);

const ChevronsRight: FC = () => (
  <svg
    className="h-3 w-3"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2.5}
      d="M13 5l7 7-7 7M5 5l7 7-7 7"
    />
  </svg>
);

const NAV_BTN =
  "inline-flex h-7 w-7 items-center justify-center rounded-none border border-slate-300 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800 disabled:pointer-events-none disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200";

const PAGE_BTN_BASE =
  "inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-none border font-mono text-[10px] font-bold uppercase tracking-[0.18em] transition-colors";

const PAGE_BTN_DEFAULT = `${PAGE_BTN_BASE} border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200`;

const PAGE_BTN_ACTIVE = `${PAGE_BTN_BASE} border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900`;

export const Pagination: FC<PaginationProps> = ({
  page,
  pageSize,
  total,
  onPageChange,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const hasNext = page < totalPages;
  const hasPrev = page > 1;
  const start = Math.min((page - 1) * pageSize + 1, total);
  const end = Math.min(page * pageSize, total);
  const pages = buildPageList(page, totalPages, 1);

  return (
    <div className="flex flex-col items-center gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:justify-between dark:border-slate-700/50">
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {start}–{end} of {total}
      </span>

      <div className="flex items-center gap-1">
        {/* First page */}
        <button
          type="button"
          className={NAV_BTN}
          disabled={!hasPrev}
          onClick={() => onPageChange(1)}
          aria-label="First page"
        >
          <ChevronsLeft />
        </button>

        {/* Previous */}
        <button
          type="button"
          className={NAV_BTN}
          disabled={!hasPrev}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
        >
          <ChevronLeft />
        </button>

        {/* Page numbers */}
        {pages.map((item, idx) =>
          item === "…" ? (
            <span
              key={`ellipsis-${idx}`}
              className="inline-flex h-7 w-5 items-center justify-center font-mono text-[10px] text-slate-400 dark:text-slate-500"
              aria-hidden
            >
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              className={item === page ? PAGE_BTN_ACTIVE : PAGE_BTN_DEFAULT}
              onClick={() => onPageChange(item)}
              aria-label={`Page ${item}`}
              aria-current={item === page ? "page" : undefined}
            >
              {item}
            </button>
          ),
        )}

        {/* Next */}
        <button
          type="button"
          className={NAV_BTN}
          disabled={!hasNext}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
        >
          <ChevronRight />
        </button>

        {/* Last page */}
        <button
          type="button"
          className={NAV_BTN}
          disabled={!hasNext}
          onClick={() => onPageChange(totalPages)}
          aria-label="Last page"
        >
          <ChevronsRight />
        </button>
      </div>
    </div>
  );
};
